/**
 * Hash Router — Manages hash-based navigation for AWENI app.
 * Routes: #/home, #/games, #/quran, #/money, #/more, #/more/settings, #/more/community
 * Supports deep linking: #/games/play/math-quest
 */

export interface Route {
  module: string;      // Module key: 'home', 'games', 'quran', 'money', 'more'
  subpath: string;     // Remaining path after module: '', 'settings', 'play/math-quest'
  full: string;        // Full hash path without #
}

export type RouteChangeHandler = (route: Route) => void;

const MODULE_MAP: Record<string, string> = {
  'home': 'home',
  'games': 'games',
  'quran': 'memoriz',
  'money': 'balancetab',
  'more': 'more',
  // Direct module routes (used by More menu navigation)
  'selecto': 'selecto',
  'blog': 'blog',
  'chat': 'chat',
  'community': 'community',
  'settings': 'settings',
  'memoriz': 'memoriz',
  'balancetab': 'balancetab',
  'family': 'family',
  // T3-1 — Family Room (WhatsApp-style closed-circle communication module).
  // v0 ships as a scaffold; full functionality lives behind /v1/family-room/* APIs.
  'family-room': 'family-room',
  'watch': 'watch',
  'weather': 'weather',
  'prayer': 'prayer',
  // Auth routes — AWENI is the unified auth entry point
  'entry': 'auth',
  'guest': 'auth',
  'claim': 'auth',
  'login': 'auth',
  'signup': 'auth',
  'forgot-password': 'auth',
  'reset-password': 'auth',
  'verify-email': 'auth',
  // Unified profile
  'profile': 'profile',
};

const DEFAULT_ROUTE = '/home';

export class Router {
  private _handlers: RouteChangeHandler[] = [];
  private _currentRoute: Route;

  constructor() {
    this._currentRoute = this._parseHash();
    window.addEventListener('hashchange', () => {
      const route = this._parseHash();
      this._currentRoute = route;
      this._notifyHandlers(route);
    });
  }

  get current(): Route {
    return this._currentRoute;
  }

  /**
   * Navigate to a hash path. Triggers route change handlers.
   */
  navigate(path: string): void {
    // Defensive normalization: callers may pass any of '/foo', 'foo',
    // '#/foo', or '#foo'. Strip a leading '#' first so we never produce
    // a double-hash URL like '##/foo' (which historically broke routing
    // for #/more/settings — see git log 2026-05-04).
    if (path.startsWith('#')) path = path.slice(1);
    if (!path.startsWith('/')) path = '/' + path;
    const newHash = '#' + path;
    if (window.location.hash === newHash) {
      // Same route — still notify (might be a refresh intent)
      this._notifyHandlers(this._parseHash());
      return;
    }
    window.location.hash = newHash;
    // hashchange event fires and handles the rest
  }

  /**
   * Register a handler for route changes.
   */
  onChange(handler: RouteChangeHandler): void {
    this._handlers.push(handler);
  }

  /**
   * Remove a route change handler.
   */
  offChange(handler: RouteChangeHandler): void {
    this._handlers = this._handlers.filter(h => h !== handler);
  }

  /**
   * Initialize the router — navigate to default if no hash present.
   */
  init(): void {
    if (!window.location.hash || window.location.hash === '#' || window.location.hash === '#/') {
      this.navigate(DEFAULT_ROUTE);
    } else {
      // Trigger handlers with current hash
      this._notifyHandlers(this._currentRoute);
    }
  }

  private _parseHash(): Route {
    let hash = window.location.hash.replace(/^#/, '');
    if (!hash || hash === '/') hash = DEFAULT_ROUTE;
    if (!hash.startsWith('/')) hash = '/' + hash;

    // Split: /module/subpath/...
    const segments = hash.split('/').filter(Boolean);
    const moduleKey = segments[0] || 'home';

    // Handle #/more/settings, #/more/community etc — resolve to the sub-module
    if (moduleKey === 'more' && segments.length > 1) {
      const subModuleKey = segments[1];
      const resolvedModule = MODULE_MAP[subModuleKey] || subModuleKey;
      const subpath = segments.slice(2).join('/');
      return { module: resolvedModule, subpath, full: hash };
    }

    const module = MODULE_MAP[moduleKey] || 'home';
    const subpath = segments.slice(1).join('/');

    return { module, subpath, full: hash };
  }

  private _notifyHandlers(route: Route): void {
    for (const handler of this._handlers) {
      handler(route);
    }
  }
}

/**
 * Factory function for Router — used by main.ts bootstrap.
 *
 * Returns a Router instance. All onChange handlers receive the full
 * `Route` object (with `module`, `subpath`, `full` fields). The previous
 * wrapper attempted to detect string-vs-object handlers via
 * `handler.length` — this was BROKEN because every handler in the
 * codebase uses one parameter, so they all received `route.full` (a
 * string) instead of the route object. That meant `route.module` was
 * undefined everywhere, masked only by the `|| 'home'` fallback in
 * App.ts which made every navigation land on home regardless of intent.
 * Bug fix dated 2026-04-30 in the SUPER AWENI rebuild.
 */
export function createRouter(): Router {
  return new Router();
}
